type CredentialDescriptorJSON = Omit<PublicKeyCredentialDescriptor, 'id'> & { id: string };

type CreationOptionsJSON = Omit<PublicKeyCredentialCreationOptions, 'challenge' | 'excludeCredentials' | 'user'> & {
    challenge: string;
    excludeCredentials?: CredentialDescriptorJSON[];
    user: Omit<PublicKeyCredentialUserEntity, 'id'> & { id: string };
};

export type RequestOptionsJSON = Omit<PublicKeyCredentialRequestOptions, 'allowCredentials' | 'challenge'> & {
    allowCredentials?: CredentialDescriptorJSON[];
    challenge: string;
};

function base64UrlToArrayBuffer(value: string): ArrayBuffer {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let value = '';
    bytes.forEach((byte) => { value += String.fromCharCode(byte); });
    return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function decodeCredentialDescriptors(credentials: CredentialDescriptorJSON[] | undefined): PublicKeyCredentialDescriptor[] | undefined {
    return credentials?.map((credential) => ({
        ...credential,
        id: base64UrlToArrayBuffer(credential.id),
    }));
}

function decodeCreationOptions(options: CreationOptionsJSON): PublicKeyCredentialCreationOptions {
    return {
        ...options,
        challenge: base64UrlToArrayBuffer(options.challenge),
        excludeCredentials: decodeCredentialDescriptors(options.excludeCredentials),
        user: {
            ...options.user,
            id: base64UrlToArrayBuffer(options.user.id),
        },
    };
}

function decodeRequestOptions(options: RequestOptionsJSON): PublicKeyCredentialRequestOptions {
    return {
        ...options,
        allowCredentials: decodeCredentialDescriptors(options.allowCredentials),
        challenge: base64UrlToArrayBuffer(options.challenge),
    };
}

function encodeResponseValue(value: ArrayBuffer | null): string | null {
    return value ? arrayBufferToBase64Url(value) : null;
}

function credentialToJSON(credential: PublicKeyCredential) {
    const response = credential.response;

    if (response instanceof AuthenticatorAttestationResponse) {
        return {
            id: credential.id,
            rawId: arrayBufferToBase64Url(credential.rawId),
            response: {
                attestationObject: arrayBufferToBase64Url(response.attestationObject),
                clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
                transports: response.getTransports?.(),
            },
            type: credential.type,
            clientExtensionResults: credential.getClientExtensionResults(),
            authenticatorAttachment: credential.authenticatorAttachment,
        };
    }

    const assertionResponse = response as AuthenticatorAssertionResponse;
    return {
        id: credential.id,
        rawId: arrayBufferToBase64Url(credential.rawId),
        response: {
            authenticatorData: arrayBufferToBase64Url(assertionResponse.authenticatorData),
            clientDataJSON: arrayBufferToBase64Url(assertionResponse.clientDataJSON),
            signature: arrayBufferToBase64Url(assertionResponse.signature),
            userHandle: encodeResponseValue(assertionResponse.userHandle),
        },
        type: credential.type,
        clientExtensionResults: credential.getClientExtensionResults(),
        authenticatorAttachment: credential.authenticatorAttachment,
    };
}

export async function createCredential(options: CreationOptionsJSON) {
    if (!window.PublicKeyCredential) throw new Error('Passkeys are not supported by this browser.');

    const credential = await navigator.credentials.create({ publicKey: decodeCreationOptions(options) });
    if (!credential) throw new Error('Passkey setup was cancelled.');

    return credentialToJSON(credential as PublicKeyCredential);
}

export async function getCredential(options: RequestOptionsJSON) {
    if (!window.PublicKeyCredential) throw new Error('Passkeys are not supported by this browser.');

    const credential = await navigator.credentials.get({ publicKey: decodeRequestOptions(options) });
    if (!credential) throw new Error('Passkey sign in was cancelled.');

    return credentialToJSON(credential as PublicKeyCredential);
}
